import { HttpResponse } from '@angular/common/http'
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  computed,
  effect,
  inject,
  linkedSignal,
} from '@angular/core'
import { rxResource, toSignal } from '@angular/core/rxjs-interop'
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms'

import { Observable, lastValueFrom, map } from 'rxjs'

import {
  FLX_MODAL_DATA,
  FlxButtonModule,
  FlxFormControlModule,
  FlxModalModule,
  FlxModalService,
} from '@flowx/angular-ui-toolkit'
import { FlxBuildTagComponent } from '@flowx/designer/modules/projects/components/build-tag/build-tag.component'
import { ApplicationApi } from '@flowx/designer/shared/api/application.api'
import { RuntimeApiService } from '@flowx/designer/shared/api/runtime.api'
import { IdeaHelperBoxComponent } from '@flowx/designer/shared/components/idea-helper-box/idea-helper-box.component'
import { FlxSpinnerModule } from '@flowx/designer/shared/components/spinner'
import { EnterSubmitFormDirective } from '@flowx/designer/shared/directives/enter-submit-form.directive'
import {
  ApplicationStatus,
  ExportHistory,
  ExportParams,
  ExportStatus,
  ExportType,
} from '@flowx/designer/shared/entities/application.entity'
import { ToastMessageService } from '@flowx/designer/shared/services/toast-message.service'
import { BranchNameComponent } from '@flowx/designer/shared/versioning/branch-name/branch-name.component'
import { BranchStateIconComponent } from '@flowx/designer/shared/versioning/branch-state-icon/branch-state-icon.component'
import { FlxBranchStateComponent } from '@flowx/designer/shared/versioning/branch-state/branch-state.component'
import { downloadHttpResponse } from '@flowx/shared/utils'

/** The version to export. */
export interface ExportedAppVersion {
  workspaceId?: string // Set when opened from a notification, whose project need not be in the page's workspace.
  id: string
  appId: string
  appName: string
  branchName?: string
  branchOrder?: number
  status?: ApplicationStatus
}

/** The build to export, addressed by its app version. Opened from a notification, its tag is not known. */
export interface ExportedBuild {
  workspaceId?: string // As for a version.
  appId: string
  applicationVersionId: string
  appName: string
  buildTag?: string
}

export type ExportModalData =
  | { exportType: ExportType.VERSION; appVersion: ExportedAppVersion }
  | { exportType: ExportType.BUILD; build: ExportedBuild }
  | { exportType: ExportType.MEDIALIBRARY }

/** How the modal reads, runs and downloads the export of a version or a build. */
interface ExportRequests {
  subject: string // Named in the error toasts.
  fileName: string
  history: () => Observable<ExportHistory>
  export: (exportParams: ExportParams) => Observable<unknown>
  download: (exportParams: ExportParams) => Observable<HttpResponse<Blob>>
}

const TITLE_BY_TYPE: Record<ExportType, string> = {
  [ExportType.BUILD]: 'Export Build',
  [ExportType.VERSION]: 'Export Version',
  [ExportType.MEDIALIBRARY]: 'Export Media Library Items',
}

/**
 * Opened through `FlxModalService` with `ExportModalData`, and closes itself. A version or build
 * export runs inside the modal; for media library items the chosen params go out through
 * `dataEventEmitter` and the opener runs the export, without closing the modal again.
 */
@Component({
  selector: 'app-export-binaries',
  imports: [
    FlxButtonModule,
    FlxModalModule,
    FlxFormControlModule,
    FormsModule,
    ReactiveFormsModule,
    FlxSpinnerModule,
    IdeaHelperBoxComponent,
    IdeaHelperBoxComponent,
    EnterSubmitFormDirective,
    FlxBuildTagComponent,
    BranchNameComponent,
    BranchStateIconComponent,
    FlxBranchStateComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './export-binaries.component.html',
  host: {
    // Workaround: flx-modal closes on Escape but lets the key go on to close the notification center
    // under this modal too. The fix belongs in flx-modal, which should stop the key itself.
    '(keydown.escape)': '$event.stopPropagation()',
  },
})
export class ExportBinariesComponent {
  #applicationApi = inject(ApplicationApi)
  #runtimeApi = inject(RuntimeApiService)
  #modalService = inject(FlxModalService)
  #toastMessageService = inject(ToastMessageService)
  #data = inject<ExportModalData>(FLX_MODAL_DATA)
  // Media library items have none; their opener runs the export.
  #exportRequests = this.#createExportRequests()

  dataEventEmitter = new EventEmitter<ExportParams>()

  exportType = this.#data.exportType
  // Media library exports don't offer a target platform version, so they never load the options.
  hasTargetPlatformVersion = this.exportType !== ExportType.MEDIALIBRARY
  modalTitle = TITLE_BY_TYPE[this.exportType]

  infoText = this.hasTargetPlatformVersion
    ? 'Choose the target FlowX.AI version and whether to include media files in the export package. The exported ZIP can only be imported into environments running the selected version.'
    : 'Choose whether to include media files in the export package.'

  // What the subtitle names: the version's branch or the build's tag; media library items have none.
  exportedVersion = this.#data.exportType === ExportType.VERSION ? this.#data.appVersion : null
  exportedBuild = this.#data.exportType === ExportType.BUILD ? this.#data.build : null

  // Every call the modal makes goes to the exported project's workspace once one is named.
  workspaceId = (this.exportedVersion ?? this.exportedBuild)?.workspaceId

  targetPlatformOptions = rxResource({
    params: () => this.hasTargetPlatformVersion || undefined,
    stream: () => this.#applicationApi.getExportTargetPlatformOptions(this.workspaceId),
  })

  // Versions and builds keep an export history; for media library items it stays idle.
  exportHistory = rxResource({
    params: () => this.#exportRequests ?? undefined,
    stream: ({ params }) => params.history().pipe(map(({ exports }) => exports)),
  })

  // Also set while an export or a download is in flight, so the modal waits for it.
  isLoading = linkedSignal(
    () => this.targetPlatformOptions.isLoading() || this.exportHistory.isLoading()
  )

  exportFG: FormGroup<{
    includeBinaries: FormControl<ExportParams['includeBinaries']>
    flowxVersion: FormControl<ExportParams['flowxVersion']>
  }> = new FormGroup({
    includeBinaries: new FormControl(true),
    flowxVersion: new FormControl(null),
  })

  exportParams = toSignal(this.exportFG.valueChanges.pipe(map(() => this.exportFG.getRawValue())), {
    initialValue: this.exportFG.getRawValue(),
  })

  selectedTargetPlatformVersion = computed(() => this.exportParams().flowxVersion)

  // The current version leads; every other one is an LTS line.
  exportTargetPlatformOptions = computed(() =>
    this.targetPlatformOptions.hasValue()
      ? this.targetPlatformOptions
          .value()
          .map((option) => ({
            ...option,
            label: option.current ? option.label : `${option.label} (LTS)`,
          }))
          .sort((a, b) => Number(b.current) - Number(a.current))
      : []
  )

  // An export is stored per target FlowX version and binaries choice, so both have to match.
  isDownloadAvailable = computed(() => {
    const { flowxVersion, includeBinaries } = this.exportParams()
    const exports = this.exportHistory.hasValue() ? this.exportHistory.value() : []

    return exports.some(
      (entry) =>
        entry.flowxVersion === flowxVersion &&
        entry.includeBinaries === includeBinaries &&
        entry.exportStatus === ExportStatus.EXPORTED
    )
  })

  isCurrentVersionSelected = computed(() => {
    const options = this.exportTargetPlatformOptions()
    const selectedVersion = this.selectedTargetPlatformVersion()
    const currentVersionOption = options.find((option) => option.current)

    if (!currentVersionOption) {
      return false
    }

    return selectedVersion === currentVersionOption.value
  })

  // Without a target platform version there are no options to require; an empty list only counts
  // once the request has settled.
  noTargetPlatformOptions = computed(
    () =>
      this.hasTargetPlatformVersion &&
      !this.targetPlatformOptions.isLoading() &&
      this.exportTargetPlatformOptions().length === 0
  )

  constructor() {
    effect(() => {
      const targetPlatformOptions = this.exportTargetPlatformOptions()

      const currentVersionOption =
        targetPlatformOptions.find((option) => option.current) ?? targetPlatformOptions[0]

      if (currentVersionOption) {
        this.exportFG.controls.flowxVersion.setValue(currentVersionOption.value)
      }
    })
  }

  close(): void {
    this.#modalService.hide()
  }

  async submitModal(): Promise<void> {
    // The history decides between Export and Download, so neither runs before it and the options
    // have loaded; a second submit waits for the first call.
    if (this.exportFG.invalid || this.noTargetPlatformOptions() || this.isLoading()) {
      return
    }

    const exportParams = this.exportFG.getRawValue()

    if (!this.#exportRequests) {
      this.dataEventEmitter.emit(exportParams)
      this.close()
      return
    }

    if (this.isDownloadAvailable()) {
      await this.#download(this.#exportRequests, exportParams)
    } else {
      // The backend reruns only a failed or timed-out export; a running or finished one is kept.
      await this.#export(this.#exportRequests, { ...exportParams, retry: true })
    }
  }

  async #download(requests: ExportRequests, exportParams: ExportParams): Promise<void> {
    try {
      this.isLoading.set(true)

      const response = await lastValueFrom(requests.download(exportParams))

      downloadHttpResponse(response, requests.fileName)
      this.close()
    } catch (error) {
      this.isLoading.set(false)
      this.#toastMessageService.showErrorMessage(
        error.error?.title || `Failed to download ${requests.subject}`
      )
    }
  }

  async #export(requests: ExportRequests, exportParams: ExportParams): Promise<void> {
    try {
      this.isLoading.set(true)
      await lastValueFrom(requests.export(exportParams))
      this.close()
    } catch (error) {
      this.isLoading.set(false)

      this.#toastMessageService.showErrorMessage(
        error.error?.title || `Failed to export ${requests.subject}`
      )
    }
  }

  // Decided once from what the modal was opened on, so nothing past this point branches on it.
  #createExportRequests(): ExportRequests | null {
    switch (this.#data.exportType) {
      case ExportType.VERSION: {
        const { workspaceId, appId, id, appName } = this.#data.appVersion

        return {
          subject: 'version',
          fileName: `${appName}.zip`,
          history: () => this.#applicationApi.getExportHistory(appId, id, workspaceId),
          export: (exportParams) =>
            this.#applicationApi.exportApplicationVersion(appId, id, exportParams, workspaceId),
          download: ({ flowxVersion, includeBinaries }) =>
            this.#applicationApi.downloadExport(
              appId,
              id,
              flowxVersion,
              includeBinaries,
              workspaceId
            ),
        }
      }
      case ExportType.BUILD: {
        const { workspaceId, appId, applicationVersionId, appName } = this.#data.build

        return {
          subject: 'build',
          fileName: `${appName}.zip`,
          history: () =>
            this.#runtimeApi.getBuildExportHistory(appId, applicationVersionId, workspaceId),
          export: ({ flowxVersion, includeBinaries, retry }) =>
            this.#runtimeApi.exportBuild(
              appId,
              applicationVersionId,
              retry,
              flowxVersion,
              includeBinaries,
              workspaceId
            ),
          download: ({ flowxVersion, includeBinaries }) =>
            this.#runtimeApi.downloadBuild(
              appId,
              applicationVersionId,
              flowxVersion,
              includeBinaries,
              workspaceId
            ),
        }
      }
      default:
        return null
    }
  }
}
